import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PersonStatus,
  isAdmin,
  type PersonSummaryDto,
  type SistradeSyncStatusDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { SimilarNames } from '../components/SimilarNames';
import { matchesQuery, normalizeText } from '../lib/search';
import { useIncremental, usePersistedState, useScrollRestore, scrollListToTop } from '../lib/list';
import { useT } from '../lib/i18n';
import { PersonCell } from '../components/PersonCell';

type SortKey =
  | 'code'
  | 'fullName'
  | 'departamento'
  | 'situacao'
  | 'contractStart'
  | 'contractEnd'
  | 'dedication'
  | 'status';
type StatusFilter = 'all' | 'active' | 'inactive';

function sortValue(p: PersonSummaryDto, key: SortKey): string {
  switch (key) {
    case 'code':
      return p.code ?? '';
    case 'fullName':
      return p.fullName ?? '';
    case 'departamento':
      return p.departamento ?? '';
    case 'situacao':
      return p.situacaoColaborador ?? '';
    case 'contractStart':
      return p.contractStart ?? '';
    case 'contractEnd':
      return p.contractEnd ?? '';
    case 'dedication':
      // Zero-pad so the numeric sort is correct and blanks sort first.
      return p.dedicationPercent != null ? String(p.dedicationPercent).padStart(6, '0') : '';
    case 'status':
      return p.status === PersonStatus.Active ? 'Ativo' : 'Inativo';
  }
}

/** ISO date (yyyy-mm-dd) → dd/mm/yyyy for display; passes other text through. */
function fmtDate(v?: string | null): string {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
}
function fmtPercent(v?: number | null): string {
  return v == null ? '—' : `${v}%`;
}

/**
 * A contract-end flag for the roster: active collaborators whose contract has
 * already ended ("expired"), or is ending within 1 / 3 / 6 months. Returns a
 * CSS modifier + a short tag, or null when nothing is worth flagging.
 */
function endFlag(
  contractEnd?: string | null,
  active = true,
): { cls: string; labelKey: string } | null {
  if (!contractEnd) return null;
  const end = Date.parse(contractEnd);
  if (Number.isNaN(end)) return null;
  const days = (end - Date.now()) / 86_400_000;
  if (days < 0) return active ? { cls: 'expired', labelKey: 'people.list.flag.expired' } : null;
  if (days <= 31) return { cls: 'm1', labelKey: 'people.list.flag.lt1' };
  if (days <= 92) return { cls: 'm3', labelKey: 'people.list.flag.lt3' };
  if (days <= 183) return { cls: 'm6', labelKey: 'people.list.flag.lt6' };
  return null;
}

export function People() {
  const { user, isManager } = useAuth();
  const t = useT();
  const admin = isAdmin(user?.roles ?? []);
  const navigate = useNavigate();
  const [all, setAll] = useState<PersonSummaryDto[]>([]);
  // Filters/sort persist across refresh and back-navigation (sessionStorage).
  const [search, setSearch] = usePersistedState('people:search', '');
  // Default view: active collaborators, externals included, ordered by name.
  const [status, setStatus] = usePersistedState<StatusFilter>('people:status', 'active');
  const [missingOnly, setMissingOnly] = usePersistedState('people:missing', false);
  const [hideExternal, setHideExternal] = usePersistedState('people:hideExternal', false);
  const [depts, setDepts] = usePersistedState<string[]>('people:depts', []);
  const [sort, setSort] = usePersistedState<{ key: SortKey; dir: 'asc' | 'desc' }>('people:sort', {
    key: 'fullName',
    dir: 'asc',
  });
  const isDefault =
    status === 'active' && !missingOnly && !hideExternal && depts.length === 0 && !search.trim();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  // Load the roster once, then filter/sort entirely on the client so the search
  // is instant, word-order independent and accent-insensitive.
  const [syncJob, setSyncJob] = useState<SistradeSyncStatusDto | null>(null);
  const syncing = syncJob?.state === 'running';

  async function reload() {
    try {
      setAll(await api.listPeople());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    let active = true;
    api
      .listPeople()
      .then((rows) => active && setAll(rows))
      .catch((err) => active && setError((err as Error).message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // Admin: pull the roster from SISTRADE, then refresh the list.
  async function syncData() {
    setError(null);
    try {
      setSyncJob(await api.startSistradeSync());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  // Poll the sync job while it runs; refresh the roster when it finishes.
  useEffect(() => {
    if (syncJob?.state !== 'running') return;
    const t = setTimeout(async () => {
      try {
        const next = await api.sistradeSyncStatus();
        setSyncJob(next);
        if (next.state === 'success') void reload();
      } catch (err) {
        setError((err as Error).message);
      }
    }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncJob]);

  // Distinct departments present in the roster (for the multi-select filter).
  const allDepartments = useMemo(
    () =>
      [...new Set(all.map((p) => p.departamento).filter(Boolean) as string[])].sort((a, b) =>
        a.localeCompare(b, 'pt'),
      ),
    [all],
  );

  // Universe = everything the external/department filters allow (status-agnostic),
  // so the status counts reflect those filters.
  const universe = useMemo(
    () =>
      all.filter((p) => {
        if (hideExternal && p.external) return false;
        if (depts.length > 0 && !(p.departamento && depts.includes(p.departamento))) return false;
        return true;
      }),
    [all, hideExternal, depts],
  );

  // Within the universe, the status selection; "dados em falta" relates to it.
  const afterStatus = useMemo(
    () =>
      universe.filter((p) => {
        if (status === 'active' && p.status !== PersonStatus.Active) return false;
        if (status === 'inactive' && p.status === PersonStatus.Active) return false;
        return true;
      }),
    [universe, status],
  );

  const rows = useMemo(() => {
    const filtered = afterStatus.filter((p) => {
      if (missingOnly && !p.missingRequired) return false;
      return matchesQuery(
        [p.fullName, p.shortName, p.code, p.departamento, p.professionalEmail, p.personalEmail]
          .filter(Boolean)
          .join(' '),
        search,
      );
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort(
      (a, b) =>
        dir *
        normalizeText(sortValue(a, sort.key)).localeCompare(
          normalizeText(sortValue(b, sort.key)),
          'pt',
          {
            numeric: true,
            sensitivity: 'base',
          },
        ),
    );
  }, [afterStatus, search, missingOnly, sort]);

  // Render 50 rows at a time, growing by 50 as the user nears the end; the depth
  // and the scroll position survive a refresh/back. (Project rule for lists.)
  const { visible, sentinelRef, reset: resetVisible } = useIncremental(rows.length, 'people:count');
  useScrollRestore('people:scroll', !loading);
  const shown = useMemo(() => rows.slice(0, visible), [rows, visible]);

  // Changing a filter/sort restarts the list from the top and the first page.
  function onSearch(v: string) {
    setSearch(v);
    resetVisible();
    scrollListToTop();
  }
  function onStatus(v: StatusFilter) {
    setStatus(v);
    resetVisible();
    scrollListToTop();
  }
  function onMissing(v: boolean) {
    setMissingOnly(v);
    resetVisible();
    scrollListToTop();
  }
  function onHideExternal(v: boolean) {
    setHideExternal(v);
    resetVisible();
    scrollListToTop();
  }
  function onDepts(v: string[]) {
    setDepts(v);
    resetVisible();
    scrollListToTop();
  }
  function resetFilters() {
    setStatus('active');
    setMissingOnly(false);
    setHideExternal(false);
    setDepts([]);
    setSearch('');
    resetVisible();
    scrollListToTop();
  }
  function toggleSort(key: SortKey) {
    setSort(
      sort.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
    resetVisible();
    scrollListToTop();
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const person = await api.createPerson({ fullName: newName.trim() });
      navigate(`/people/${person.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const counts = useMemo(
    () => ({
      all: universe.length,
      active: universe.filter((p) => p.status === PersonStatus.Active).length,
      inactive: universe.filter((p) => p.status !== PersonStatus.Active).length,
      // "Dados em falta" relates to the current status selection.
      missing: afterStatus.filter((p) => p.missingRequired).length,
    }),
    [universe, afterStatus],
  );

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('people.list.title')}</h2>
          <p className="page-sub">
            {loading
              ? t('common.loading')
              : t(counts.all === 1 ? 'people.list.countOne' : 'people.list.countMany', {
                  n: counts.all,
                })}
          </p>
        </div>
        <div className="spacer" />
        {admin && (
          <button
            type="button"
            className="secondary"
            onClick={() => void syncData()}
            disabled={syncing}
            title={t('people.list.syncTitle')}
          >
            {syncing ? t('people.list.syncing') : t('people.list.sync')}
          </button>
        )}
        {admin && (
          <Link to="/people/eliminados">
            <button type="button" className="secondary">
              {t('people.list.deleted')}
            </button>
          </Link>
        )}
        {isManager && (
          <button type="button" onClick={() => setCreating((v) => !v)}>
            {t('people.list.new')}
          </button>
        )}
      </div>

      {syncJob?.state === 'success' && syncJob.result && (
        <div className="alert ok" style={{ marginBottom: 12 }}>
          {t('people.list.syncDone', {
            total: syncJob.result.total,
            created: syncJob.result.created,
            updated: syncJob.result.updated,
          })}
        </div>
      )}
      {syncJob?.state === 'error' && (
        <div className="alert error" style={{ marginBottom: 12 }}>
          {t('people.list.syncFailed', {
            error: syncJob.error ?? t('people.list.unknownError'),
          })}
        </div>
      )}

      {creating && isManager && (
        <form className="filters" onSubmit={create} style={{ marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            autoFocus
            placeholder={t('people.list.newNamePlaceholder')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button type="submit" disabled={busy}>
            {t('people.list.create')}
          </button>
          <button type="button" className="secondary" onClick={() => setCreating(false)}>
            {t('common.cancel')}
          </button>
          <SimilarNames name={newName} />
        </form>
      )}

      {error && <div className="alert error">{error}</div>}

      <div className="erp-panel">
        <div className="erp-toolbar">
          <div className="erp-filters">
            <div className="erp-seg" role="group" aria-label={t('people.list.filterByStatus')}>
              {(
                [
                  ['active', t('people.list.statusActive'), counts.active],
                  ['all', t('people.list.statusAll'), counts.all],
                  ['inactive', t('people.list.statusInactive'), counts.inactive],
                ] as [StatusFilter, string, number][]
              ).map(([key, label, n]) => (
                <button
                  key={key}
                  type="button"
                  className={status === key ? 'on' : ''}
                  onClick={() => onStatus(key)}
                >
                  {label} <span className="seg-count">{n}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`filter-chip${missingOnly ? ' on' : ''}`}
              onClick={() => onMissing(!missingOnly)}
              title={t('people.list.missingTitle')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
              </svg>
              {t('people.list.missing')} <span className="seg-count">{counts.missing}</span>
            </button>
            <button
              type="button"
              className={`filter-chip${hideExternal ? ' on' : ''}`}
              onClick={() => onHideExternal(!hideExternal)}
              title={t('people.list.hideExternalTitle')}
            >
              {t('people.list.hideExternal')}
            </button>
            <DeptFilter options={allDepartments} selected={depts} onChange={onDepts} />
            {!isDefault && (
              <button type="button" className="filter-chip ghost" onClick={resetFilters}>
                {t('people.list.clearFilters')}
              </button>
            )}
          </div>
          <label className="erp-search">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14M21 21l-5-5" />
            </svg>
            <input
              type="search"
              placeholder={t('people.list.searchPlaceholder')}
              value={search}
              onChange={(e) => onSearch(e.target.value)}
            />
          </label>
        </div>

        {loading ? (
          <div className="panel-empty">{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="panel-empty is-empty">{t('people.list.empty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="grid sortable-grid">
              <thead>
                <tr>
                  <SortTh
                    label={t('people.list.col.code')}
                    col="code"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortTh
                    label={t('people.list.col.name')}
                    col="fullName"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortTh
                    label={t('people.list.col.department')}
                    col="departamento"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortTh
                    label={t('people.list.col.situation')}
                    col="situacao"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortTh
                    label={t('people.list.col.contractStart')}
                    col="contractStart"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortTh
                    label={t('people.list.col.contractEnd')}
                    col="contractEnd"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortTh
                    label={t('people.list.col.dedication')}
                    col="dedication"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortTh
                    label={t('people.list.col.state')}
                    col="status"
                    sort={sort}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/people/${p.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{p.code ?? '—'}</td>
                    <td>
                      <PersonCell name={p.fullName} sub={p.preferredEmail}>
                        <Link to={`/people/${p.id}`} onClick={(e) => e.stopPropagation()}>
                          {p.fullName}
                        </Link>
                        {p.missingRequired && (
                          <span
                            className="miss-dot"
                            title={t('people.list.missDotTitle')}
                            aria-label={t('people.list.missDotAria')}
                          />
                        )}
                      </PersonCell>
                    </td>
                    <td>{p.departamento ?? '—'}</td>
                    <td>{p.situacaoColaborador ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {fmtDate(p.contractStart)}
                      {p.external && <span className="cell-sub">{t('people.list.external')}</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {fmtDate(p.contractEnd)}
                      {(() => {
                        const f = endFlag(p.contractEnd, p.status === PersonStatus.Active);
                        return f ? (
                          <span className={`end-tag ${f.cls}`}>{t(f.labelKey)}</span>
                        ) : null;
                      })()}
                    </td>
                    <td>{fmtPercent(p.dedicationPercent)}</td>
                    <td>
                      <span
                        className={`badge ${p.status === PersonStatus.Active ? 'approved' : 'synced'}`}
                      >
                        {p.status === PersonStatus.Active
                          ? t('shared.active')
                          : t('shared.inactive')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Sentinel: as it nears the viewport, another page of 50 loads. */}
            {visible < rows.length && (
              <div ref={sentinelRef} className="list-sentinel">
                {t('people.list.showing', { visible, total: rows.length })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function SortTh({
  label,
  col,
  sort,
  onSort,
}: {
  label: string;
  col: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === col;
  return (
    <th className={`sortable${active ? ' sorted' : ''}`} onClick={() => onSort(col)}>
      {label}
      <span className="sort-arrow">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

/** A pill that opens a searchable multi-select of departments. */
function DeptFilter({
  options,
  selected,
  onChange,
}: {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = query.trim();
  const shown = q ? options.filter((o) => matchesQuery(o, q)) : options;
  function toggle(dep: string) {
    onChange(selected.includes(dep) ? selected.filter((d) => d !== dep) : [...selected, dep]);
  }

  return (
    <div className="combo" ref={ref} style={{ marginTop: 0 }}>
      <button
        type="button"
        className={`filter-chip${selected.length > 0 ? ' on' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {t('people.list.dept.label')}
        {selected.length > 0 && <span className="seg-count">{selected.length}</span>}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="combo-pop">
          <div className="combo-search">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14M21 21l-5-5" />
            </svg>
            <input
              value={query}
              placeholder={t('people.list.dept.filterPlaceholder')}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {selected.length > 0 && (
            <button type="button" className="combo-clear" onClick={() => onChange([])}>
              {t('people.list.dept.clear', { n: selected.length })}
            </button>
          )}
          <ul className="combo-list" role="listbox" aria-multiselectable="true">
            {shown.map((dep) => (
              <li
                key={dep}
                role="option"
                aria-selected={selected.includes(dep)}
                className="combo-opt check-opt"
                onClick={() => toggle(dep)}
              >
                <input type="checkbox" readOnly checked={selected.includes(dep)} />
                <span className="combo-opt-label">{dep}</span>
              </li>
            ))}
            {shown.length === 0 && <li className="combo-empty">{t('people.list.dept.empty')}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
